import type { ReactNode } from 'react';
import { compareOptional, ResultsSort } from '../../shared/results_page/results_sort.js';
import type { RentalFilterableOffer } from './rental_results_filters.js';

/** Os critérios do carro (#5125): o recomendado da VOLL, o total do período,
 *  a distância da agência ao lugar buscado (calculada aqui, nunca exibida),
 *  a categoria e a agência (locadora) em ordem alfabética. */
export type RentalResultsOrder = 'recommended' | 'price' | 'distance' | 'category' | 'company';

export const RENTAL_RESULTS_ORDERS: readonly RentalResultsOrder[] = [
  'recommended',
  'price',
  'distance',
  'category',
  'company',
];

export const DEFAULT_RENTAL_RESULTS_ORDER: RentalResultsOrder = 'recommended';

/** Os critérios que valem para ESTAS ofertas: `distance` só aparece quando
 *  alguma tem distância. Sem o lugar buscado (o diálogo da conversa não tem
 *  coordenada) ou sem coordenada de agência, ela cairia inteira no desempate
 *  pelo total e repetiria o `price` com outro nome. */
export function rentalResultsOrdersOf(
  offers: readonly RentalFilterableOffer[],
): readonly RentalResultsOrder[] {
  return offers.some((facts) => facts.distance !== undefined)
    ? RENTAL_RESULTS_ORDERS
    : RENTAL_RESULTS_ORDERS.filter((order) => order !== 'distance');
}

/** A ordem escolhida que deixou de valer (Distância sem nenhuma oferta com
 *  distância) volta ao padrão: a lista, o ordenador e o chip dizem a mesma. */
export function availableRentalOrder(
  order: RentalResultsOrder,
  offers: readonly RentalFilterableOffer[],
): RentalResultsOrder {
  return rentalResultsOrdersOf(offers).includes(order) ? order : DEFAULT_RENTAL_RESULTS_ORDER;
}

export type RentalResultsSortCopy = {
  sortLabel: string;
  orderLabel(order: RentalResultsOrder): string;
};

/** Pelo EIXO do selo (`icon`), não pelo `id` — ver o ordenador do hotel. */
const isRecommended = (facts: RentalFilterableOffer) =>
  facts.offer.badges.some((badge) => badge.icon === 'recommended');

/**
 * A ordenação, pura. `recommended` NÃO é "a ordem que veio": puxa o
 * recomendado para o topo e o `sort` estável do JS preserva a curadoria atrás
 * dele — quem não tem selo nunca sobe por engano (a mesma regra do hotel).
 * `price` é o TOTAL do período — o número que o card mostra. `distance` põe
 * a agência sem coordenada no fim. `category` e `company` agrupam pelo rótulo
 * em ordem alfabética e, dentro do grupo, o menor total vem antes.
 *
 * As preferências do viajante só desempatam: o critério escolhido manda (no
 * `price`, o mais barato vem antes mesmo sem preferência) e, entre os empatados
 * nele, os que atendem mais preferências vêm na frente — no `recommended`, dentro
 * do grupo com selo e do grupo sem, sem mexer na curadoria entre iguais (o `sort`
 * é estável). Nunca filtra: o carro que não atende continua na lista.
 *
 * Tudo isso DENTRO de cada página da busca (`page`): a rolagem infinita só
 * acrescenta, e uma página nova que reordenasse o que já está em cena faria a
 * lista pular debaixo do dedo (#4513: o recomendado da 2ª página subia para o
 * topo; no preço, um carro barato fora da política saltava acima dos já
 * lidos). O servidor pagina numa ordem só — dentro da política, depois o
 * menor total —, então página a página a lista segue coerente.
 */
export function sortRentalOffers(
  offers: readonly RentalFilterableOffer[],
  order: RentalResultsOrder,
): RentalFilterableOffer[] {
  const byPreference = (a: RentalFilterableOffer, b: RentalFilterableOffer) =>
    (b.preferenceRank ?? 0) - (a.preferenceRank ?? 0);
  const byPage = (a: RentalFilterableOffer, b: RentalFilterableOffer) =>
    (a.page ?? 1) - (b.page ?? 1);
  const byLabel = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  const byTotal = (a: RentalFilterableOffer, b: RentalFilterableOffer) =>
    compareOptional(a.totalPrice, b.totalPrice, 'asc');
  const sorted = [...offers];
  switch (order) {
    case 'recommended':
      return sorted.sort(
        (a, b) =>
          byPage(a, b) || Number(isRecommended(b)) - Number(isRecommended(a)) || byPreference(a, b),
      );
    case 'price':
      return sorted.sort((a, b) => byPage(a, b) || byTotal(a, b) || byPreference(a, b));
    case 'distance':
      return sorted.sort(
        (a, b) =>
          byPage(a, b) ||
          compareOptional(a.distance, b.distance, 'asc') ||
          byTotal(a, b) ||
          byPreference(a, b),
      );
    case 'category':
      return sorted.sort(
        (a, b) =>
          byPage(a, b) ||
          byLabel(a.offer.categoryLabel, b.offer.categoryLabel) ||
          byTotal(a, b) ||
          byPreference(a, b),
      );
    case 'company':
      return sorted.sort(
        (a, b) =>
          byPage(a, b) ||
          byLabel(a.offer.company.name, b.offer.company.name) ||
          byTotal(a, b) ||
          byPreference(a, b),
      );
    default: {
      const exhaustive: never = order;
      return exhaustive;
    }
  }
}

/** O ordenador do carro sobre o `ResultsSort` da casca. */
export function RentalResultsSort({
  value,
  offers,
  copy,
  disabled,
  onChange,
}: {
  readonly value: RentalResultsOrder;
  /** As ofertas em cena: delas sai quais critérios valem (`rentalResultsOrdersOf`). */
  readonly offers: readonly RentalFilterableOffer[];
  readonly copy: RentalResultsSortCopy;
  readonly disabled?: boolean;
  readonly onChange: (order: RentalResultsOrder) => void;
}): ReactNode {
  return (
    <ResultsSort
      value={value}
      orders={rentalResultsOrdersOf(offers)}
      sortLabel={copy.sortLabel}
      orderLabel={(order) => copy.orderLabel(order)}
      disabled={disabled}
      onChange={onChange}
    />
  );
}
